/**
 * Every assumption about D365 that must be confirmed against the live
 * environment (`/data/$metadata`) lives in this file. Each item is mirrored in
 * docs/VERIFY.md. Do not hardcode these names anywhere else.
 */
import { CUSTOMER, PROJECT, PROJECT_CONTRACT, PROJECT_FUNDING_SOURCE, VENDOR } from './entities.js';

/** OData entity sets (spec 12.2). VERIFY each against $metadata. */
export const ENTITY_SETS = {
  legalEntities: 'LegalEntities',
  systemUsers: 'SystemUsers',
  securityRoles: 'SecurityRoles',
  securityUserRoles: 'SecurityUserRoles',
  securityUserRoleOrganizations: 'SecurityUserRoleOrganizations',
  vendors: 'VendorsV2',
  workers: 'Workers',
  employments: 'Employments',
  positions: 'Positions',
  positionHierarchies: 'PositionHierarchies',
  positionWorkerAssignments: 'PositionWorkerAssignments',
  jobs: 'Jobs',
  financialDimensionValues: 'FinancialDimensionValues',
  // Not yet identified — confirm in $metadata before Phase 2 live work.
  signingLimits: 'VERIFY_SigningLimits',
  policyOrganizations: 'VERIFY_PolicyOrganizations',
  workflowParameters: 'VERIFY_WorkflowParameters',
  legalEntityAddresses: 'VERIFY_LegalEntityPostalAddresses',
  taxRegistrations: 'VERIFY_TaxRegistrations',
} as const;

/**
 * Entities copied by the LG-VENUE-COPY project, used for the "Copied setup"
 * record-count check (spec 11.4). VERIFY: replace with the real project's
 * entity list once exported from the sandbox.
 */
export const COPIED_ENTITY_SETS = [
  'MainAccounts',
  'FinancialDimensionValues',
  'NumberSequenceReferences',
  'VendorGroups',
  'PaymentTerms',
  'MethodsOfPayment',
  'ProcurementCategories',
  'SalesTaxGroups',
  'VendorPostingProfiles',
  'ProjectGroups',
  'ProjectCategories',
  'VendorsV2',
] as const;

/** Policy names used for addCompanyToPolicy (spec 11.1 step 8). VERIFY. */
export const POLICY_NAMES = {
  SigningLimit: 'VERIFY: signing limit policy name',
  Purchasing: 'VERIFY: purchasing policy name',
} as const;

/** Positions are assumed global across companies (rule P7). VERIFY. */
export const POSITIONS_ARE_GLOBAL = true;

/** Custom X++ service group path (spec 12.5). Confirm with the D365 developer. */
export const LG_SERVICE_GROUP = 'LGVenueServices';

// ── Data management packages ─────────────────────────────────────────────────
//
// VERIFY all of this by exporting the data projects once from the sandbox:
// entity names (as shown in Data management), staging column names, the
// Manifest.xml / PackageHeader.xml layout and the CSV date/boolean formats.
// Replace the values below with what the export contains.

/** Data projects (definition groups). The recurring data job points at these. */
export const DMF_PROJECTS = {
  setup: 'LG-VENUE-SETUP',
  people: 'LG-VENUE-PEOPLE',
  projects: 'LG-VENUE-PROJECTS',
  /** Ledger setup for a new company (built from a template profile, or a retargeted export of the template). */
  ledger: 'LG-VENUE-LEDGER',
} as const;

/**
 * Entities to export from the template company to build a new company's ledger starting point,
 * in import order. Names, target entities and columns CONFIRMED 2026-10-22 from an export of the
 * demo environment (data project RSM-Legal-entity-export). `inStarter`: also in the synthetic starter.
 */
export const LEDGER_EXPORT_ENTITIES: ReadonlyArray<{
  entityName: string;
  targetEntity: string;
  why: string;
  inStarter: boolean;
}> = [
  {
    entityName: 'Ledger',
    targetEntity: 'LedgerEntity',
    why: "The company's ledger: chart of accounts, fiscal calendar, currencies, exchange rate types and account structures (ACCOUNTSTRUCTURENAME1–20)",
    inStarter: true,
  },
  {
    entityName: 'Ledger fiscal calendar period V2',
    targetEntity: 'LedgerFiscalPeriodV2Entity',
    why: "Period status per company (open the test journal's period)",
    inStarter: true,
  },
  {
    entityName: 'Number sequence code V2',
    targetEntity: 'NumberSequenceTableV2Entity',
    why: 'Journal batch and voucher sequences, scoped to the company',
    inStarter: true,
  },
  {
    entityName: 'Number sequence references V2',
    targetEntity: 'NumberSequencesReferenceV2Entity',
    why: 'Which sequence each GL reference uses',
    inStarter: true,
  },
  {
    entityName: 'Ledger parameters',
    targetEntity: 'LedgerParametersEntity',
    why: 'Company GL parameters',
    inStarter: false,
  },
  {
    entityName: 'Accounts for automatic transactions',
    targetEntity: 'LedgerAutomaticTransactionAccountEntity',
    why: 'System accounts posting needs (errors, rounding, penny differences)',
    inStarter: false,
  },
  {
    entityName: 'Calendar',
    targetEntity: 'WorkCalendarEntity',
    why: "Work calendars: the workflow's due-date calendar (24hr) is company-specific",
    inStarter: true,
  },
  {
    entityName: 'Journal names',
    targetEntity: 'LedgerJournalNameEntity',
    why: 'GL journal names, voucher series, approval workflow link (WORKFLOWID)',
    inStarter: true,
  },
  {
    entityName: 'Main account legal entity overrides',
    targetEntity: 'MainAccountLegalEntityOverrideEntity',
    why: 'Overlay C: suspended accounts and default dimensions for this company only',
    inStarter: true,
  },
  {
    entityName: 'Journal Control V2',
    targetEntity: 'LedgerJournalControlV2Entity',
    why: 'Overlay B: which account range a journal name may post to',
    inStarter: true,
  },
];

/**
 * Staging columns for the master data entities: the OData field names (entities.ts, V-W7)
 * in upper case, so the import file and the OData path stay one definition. Fields not
 * confirmed yet (null) are left out of both.
 */
export const odataColumns = (fields: Record<string, string | null>): string[] =>
  Object.values(fields).flatMap((f) => (f ? [f.toUpperCase()] : []));

export interface DmfEntityDef {
  /** Entity name as listed in Data management */
  entityName: string;
  /** File name inside the package (written as .xlsx, like the export) */
  fileName: string;
  /** Internal entity name (manifest TargetEntity), when confirmed */
  targetEntity?: string;
  columns: readonly string[];
}

export const DMF_ENTITIES = {
  legalEntities: {
    entityName: 'Legal entities',
    fileName: 'Legal entities.csv',
    columns: [
      'LEGALENTITYID',
      'NAME',
      'SEARCHNAME',
      'ADDRESSDESCRIPTION',
      'ADDRESSSTREET',
      'ADDRESSCITY',
      'ADDRESSSTATE',
      'ADDRESSZIPCODE',
      'ADDRESSCOUNTRYREGIONID',
      'TIMEZONE',
    ],
  },
  dimensionValues: {
    entityName: 'Financial dimension values',
    fileName: 'Financial dimension values.csv',
    columns: ['DIMENSIONNAME', 'DIMENSIONVALUE', 'DESCRIPTION', 'ISSUSPENDED'],
  },
  // People entities: names and targets CONFIRMED 2026-10-27 against the Legends TRN data entity list
  // (DataManagementEntities), columns against its $metadata. An entity name D365 doesn't know is
  // dropped from the import project without a message, which is how "Users", "Workers", … were lost.
  users: {
    entityName: 'User information',
    fileName: 'User information.csv',
    targetEntity: 'SystemUserEntity',
    columns: [
      'USERID',
      'USERNAME',
      'ALIAS',
      'EMAIL',
      'NETWORKDOMAIN',
      'ENABLED',
      'COMPANY',
      'USERINFO_LANGUAGE',
      'HELPLANGUAGE',
      'ACCOUNTTYPE',
    ],
  },
  // USER links the worker's person to the D365 user (no separate person-user file: that entity is
  // keyed by party number, which D365 assigns on import). WORKERTYPE comes from the employment.
  workers: {
    entityName: 'Worker',
    fileName: 'Worker.csv',
    targetEntity: 'HcmWorkerEntity',
    columns: [
      'PERSONNELNUMBER',
      'FIRSTNAME',
      'LASTNAME',
      'PRIMARYCONTACTEMAIL',
      'USER',
      'PERSONUSERVALIDFROM',
    ],
  },
  employments: {
    entityName: 'Employment',
    fileName: 'Employment.csv',
    targetEntity: 'HcmEmploymentEntity',
    columns: ['PERSONNELNUMBER', 'LEGALENTITYID', 'EMPLOYMENTSTARTDATE', 'WORKERTYPE'],
  },
  jobs: {
    entityName: 'Jobs',
    fileName: 'Jobs.csv',
    targetEntity: 'HcmJobEntity',
    columns: ['JOBID', 'DESCRIPTION'],
  },
  positions: {
    entityName: 'Positions',
    fileName: 'Positions.csv',
    targetEntity: 'HcmPositionEntity',
    columns: [
      'POSITIONID',
      'DESCRIPTION',
      'JOBID',
      'DEPARTMENTNUMBER',
      'AVAILABLEFORASSIGNMENT',
      'PAIDBYLEGALENTITY',
      'PAYCYCLEID',
    ],
  },
  positionHierarchies: {
    entityName: 'Position hierarchies',
    fileName: 'Position hierarchies.csv',
    targetEntity: 'HcmPositionHierarchyEntity',
    columns: ['POSITIONID', 'PARENTPOSITIONID', 'HIERARCHYTYPENAME', 'VALIDFROM', 'VALIDTO'],
  },
  positionAssignments: {
    entityName: 'Position worker assignments',
    fileName: 'Position worker assignments.csv',
    targetEntity: 'HcmPositionWorkerAssignmentEntity',
    columns: ['POSITIONID', 'PERSONNELNUMBER', 'VALIDFROM', 'VALIDTO', 'ISPRIMARYPOSITION'],
  },
  userRoles: {
    entityName: 'Security user role association',
    fileName: 'Security user role association.csv',
    targetEntity: 'SystemSecurityUserRoleAssociationEntity',
    columns: ['USERID', 'SECURITYROLEIDENTIFIER', 'SECURITYROLENAME', 'ASSIGNMENTSTATUS', 'ASSIGNMENTMODE'],
  },
  // VERIFY: of TRN's two role-organization entities, this is the one with the OData
  // SecurityUserRoleOrganizations fields (OrganizationType, OrganizationId, OperatingUnitType).
  userRoleOrganizations: {
    entityName: 'System security user role organization assignment',
    fileName: 'System security user role organization assignment.csv',
    targetEntity: 'SystemSecurityUserRoleOrganizationEntity',
    columns: ['USERID', 'SECURITYROLEIDENTIFIER', 'ORGANIZATIONTYPE', 'ORGANIZATIONID', 'OPERATINGUNITTYPE'],
  },
  // No D365 entity has this name (TRN, 2026-10-27): D365 skips the file and signing limits are set
  // up by hand. Kept because the mock reads it back for the post-provisioning checks.
  signingLimits: {
    entityName: 'Signing limits',
    fileName: 'Signing limits.csv',
    columns: ['COMPANY', 'JOBID', 'DOCUMENTTYPE', 'APPROVALLIMIT', 'CURRENCYCODE', 'EFFECTIVEDATE'],
  },
  customers: {
    entityName: 'Customers V3',
    fileName: 'Customers V3.csv',
    columns: odataColumns(CUSTOMER.fields),
  },
  vendors: {
    entityName: 'Vendors V2',
    fileName: 'Vendors V2.csv',
    columns: odataColumns(VENDOR.fields),
  },
  // Names CONFIRMED 2026-10-27 against the TRN data entity list ("Project contracts" and "Project
  // funding sources" don't exist there).
  projectContracts: {
    entityName: 'Project contract headers',
    fileName: 'Project contract headers.csv',
    targetEntity: 'ProjProjectContractHeaderEntity',
    columns: odataColumns(PROJECT_CONTRACT.fields),
  },
  projectFundingSources: {
    entityName: 'Project funding source',
    fileName: 'Project funding source.csv',
    targetEntity: 'ProjFundingSourceEntity',
    columns: odataColumns(PROJECT_FUNDING_SOURCE.fields),
  },
  projects: {
    entityName: 'Projects',
    fileName: 'Projects.csv',
    targetEntity: 'ProjectEntity',
    columns: odataColumns(PROJECT.fields),
  },
  // Ledger starter (LG-VENUE-LEDGER). Names and staging columns CONFIRMED 2026-10-22 from the export
  // (each entity has more columns; these are the ones the starter fills, the rest take defaults).
  ledger: {
    entityName: 'Ledger',
    fileName: 'Ledger.csv',
    targetEntity: 'LedgerEntity',
    columns: [
      'LEGALENTITYID',
      'NAME',
      'DESCRIPTION',
      'CHARTOFACCOUNTS',
      'FISCALCALENDAR',
      'ACCOUNTINGCURRENCY',
      'REPORTINGCURRENCY',
      'EXCHANGERATETYPE',
      'BUDGETEXCHANGERATETYPE',
      'ACCOUNTSTRUCTURENAME1',
    ],
  },
  ledgerFiscalPeriods: {
    entityName: 'Ledger fiscal calendar period V2',
    fileName: 'Ledger fiscal calendar period V2.csv',
    targetEntity: 'LedgerFiscalPeriodV2Entity',
    columns: ['LEDGERNAME', 'CALENDAR', 'YEARNAME', 'PERIODNAME', 'LEGALENTITYID', 'PERIODSTATUS'],
  },
  numberSequenceCodes: {
    entityName: 'Number sequence code V2',
    fileName: 'Number sequence code V2.csv',
    targetEntity: 'NumberSequenceTableV2Entity',
    columns: [
      'NUMBERSEQUENCECODE',
      'SCOPETYPE',
      'SCOPEVALUE',
      'COMPANY',
      'NAME',
      'SMALLEST',
      'LARGEST',
      'NEXT',
      'FORMAT',
      'MANUAL',
      'CONTINUOUS',
    ],
  },
  numberSequenceReferences: {
    entityName: 'Number sequence references V2',
    fileName: 'Number sequence references V2.csv',
    targetEntity: 'NumberSequencesReferenceV2Entity',
    columns: ['SCOPETYPE', 'SCOPEVALUE', 'DATATYPENAME', 'NUMBERSEQUENCECODE'],
  },
  workCalendars: {
    entityName: 'Calendar',
    fileName: 'Calendar.csv',
    targetEntity: 'WorkCalendarEntity',
    columns: [
      'CALENDARID',
      'CALENDARNAME',
      'WORKHOURS',
      'ISMONDAYWORKINGDAY',
      'ISTUESDAYWORKINGDAY',
      'ISWEDNESDAYWORKINGDAY',
      'ISTHURSDAYWORKINGDAY',
      'ISFRIDAYWORKINGDAY',
      'ISSATURDAYWORKINGDAY',
      'ISSUNDAYWORKINGDAY',
    ],
  },
  journalNames: {
    entityName: 'Journal names',
    fileName: 'Journal names.csv',
    targetEntity: 'LedgerJournalNameEntity',
    columns: [
      'NAME',
      'DESCRIPTION',
      'TYPE',
      'VOUCHERSERIESCODE',
      'VOUCHERSERIESCOMPANYID',
      'ISWORKFLOWAPPROVALACTIVE',
      'WORKFLOWID',
    ],
  },
  mainAccountOverrides: {
    entityName: 'Main account legal entity overrides',
    fileName: 'Main account legal entity overrides.csv',
    targetEntity: 'MainAccountLegalEntityOverrideEntity',
    columns: [
      'MAINACCOUNTID',
      'CHARTOFACCOUNTS',
      'LEGALENTITYID',
      'ISSUSPENDED',
      'ACTIVEFROM',
      'ACTIVETO',
      'DEFAULTDIMENSIONDISPLAYVALUE',
    ],
  },
  journalControls: {
    entityName: 'Journal Control V2',
    fileName: 'Journal Control V2.csv',
    targetEntity: 'LedgerJournalControlV2Entity',
    columns: [
      'JOURNALNAME',
      'ACCOUNTTYPE',
      'JOURNALLEGALENTITY',
      'LEDGERLEGALENTITY',
      'ACCOUNTSTRUCTURE',
      'SEGMENT',
      'FROMVALUE',
      'TOVALUE',
    ],
  },
} as const satisfies Record<string, DmfEntityDef>;

/**
 * Recurring data jobs (Data management → Data project → Create recurring data
 * job). Each job has an activity ID (GUID); the app enqueues packages to
 * `POST /api/connector/enqueue/{activityId}?company={id}` and reads
 * `/api/connector/jobstatus/{activityId}?jobId={messageId}`. VERIFY the IDs
 * once the jobs exist in the sandbox; the mock accepts any value.
 */
export const RECURRING_JOBS = {
  people: { activityId: 'VERIFY-00000000-0000-0000-0000-LG-VENUE-PEOPLE', project: 'LG-VENUE-PEOPLE' },
} as const;

/** Values D365 expects in CSV cells. VERIFY against the export. */
export const DMF_FORMAT = {
  yes: 'Yes',
  no: 'No',
  /** Open-ended valid-to date */
  maxDate: '2154-12-31',
  hierarchyType: 'Line',
  workerType: 'Employee',
  networkDomain: 'https://sts.windows.net/',
  /** Language and help language for new users (VERIFY column names LANGUAGE / HELPLANGUAGE). */
  language: 'en-us',
  dimensionName: 'Venue',
  countryRegion: 'USA',
} as const;
